/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { Row, Col, Card, CardBody, Table, Button, Badge } from "reactstrap";
import Select from "react-select";
import dayjs from "dayjs";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    BarChart, Bar, PieChart, Pie, Cell, Legend, AreaChart, Area
} from 'recharts';

const PaymentReport = () => {
    const dispatch = useDispatch();
    const [reportData, setReportData] = useState(null);

    // Filters
    const [selectedMethod, setSelectedMethod] = useState(null);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);

    const paymentMethods = [
        { value: "Cash", label: "Cash" },
        { value: "Cheque", label: "Cheque" },
        { value: "Bank Transfer", label: "Bank Transfer" },
        { value: "PayNow", label: "PayNow" }
    ];

    const loadReport = async () => {
        dispatch(loadingTrue());
        let url = `/reports/payments.php?1=1`; // Machan un file path check panniko
        if (selectedMethod) url += `&payment_method=${selectedMethod.value}`;
        if (fromDate) url += `&from_date=${fromDate.format("YYYY-MM-DD")}`;
        if (toDate) url += `&to_date=${toDate.format("YYYY-MM-DD")}`;

        const res = await Handler({ method: "get", url });
        if (res?.success) {
            setReportData(res.data);
        }
        dispatch(loadingFalse());
    };

    useEffect(() => {
        loadReport();
    }, []);

    if (!reportData) return null;

    const { summary, payment_method_chart, payment_trend_chart, invoice_payment_chart, list } = reportData;

    // Data Formatting for Charts
    const formattedPieData = payment_method_chart?.map(item => ({
        name: item.payment_method,
        value: parseFloat(item.total || 0)
    })) || [];

    const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#3b82f6', '#8b5cf6', '#ef4444'];

    return (
        <div className="report-shell p-4 min-vh-100" style={{ backgroundColor: '#f8fafc' }}>

            {/* Header Area */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-5 gap-3">
                <div>
                    <h2 className="fw-bold text-slate-900 mb-0 tracking-tight">Revenue Analytics</h2>
                    <p className="text-slate-500 font-medium">Detailed tracking of incoming payments and cash flow</p>
                </div>

                {/* Modern Filter Bar */}
                <div className="shadow-sm d-flex align-items-center gap-2 p-2 bg-white rounded-pill border">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <div style={{ width: 180 }}>
                            <Select
                                options={paymentMethods}
                                value={selectedMethod}
                                onChange={setSelectedMethod}
                                placeholder="Method"
                                isClearable
                                styles={{ control: (b) => ({ ...b, border: 0, boxShadow: 'none' }) }}
                            />
                        </div>
                        <div className="vr mx-2" style={{ height: 20 }}></div>
                        <DatePicker value={fromDate} onChange={setFromDate} slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 110 } } }} />
                        <span className="text-slate-300 mx-1">-</span>
                        <DatePicker value={toDate} onChange={setToDate} slotProps={{ textField: { variant: 'standard', InputProps: { disableUnderline: true }, sx: { width: 110 } } }} />
                        <Button color="indigo" className="rounded-pill px-4 fw-bold text-white shadow-sm ms-2" style={{ backgroundColor: '#6366f1', border: 0 }} onClick={loadReport}>
                            Fetch
                        </Button>
                    </LocalizationProvider>
                </div>
            </div>

            {/* ⭐ Summary Stats */}
            <Row className="mb-5 g-4">
                <Col lg={4}>
                    <div className="p-4 rounded-4 bg-white border-0 shadow-sm d-flex align-items-center justify-content-between">
                        <div>
                            <span className="text-slate-400 small uppercase fw-bold ls-1">Total Collections</span>
                            <h2 className="fw-bold text-slate-900 mb-0 mt-1">{summary?.total_payments || 0}</h2>
                        </div>
                        <div className="bg-indigo-50 text-indigo-600 rounded-circle p-3"><i className="bi bi-collection-fill h3 mb-0"></i></div>
                    </div>
                </Col>
                <Col lg={8}>
                    <div className="p-4 rounded-4 bg-indigo-600 border-0 shadow-lg d-flex align-items-center justify-content-between">
                        <div>
                            <span className="opacity-75 small uppercase fw-bold ls-1">Total Revenue Received</span>
                            <h2 className="fw-bold mb-0 mt-1">
                                SGD {parseFloat(summary?.total_received || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </h2>
                        </div>
                        <div className="bg-white bg-opacity-20 rounded-circle p-3"><i className="bi bi-currency-dollar h3 mb-0"></i></div>
                    </div>
                </Col>
            </Row>

            {/* ⭐ Charts Row */}
            <Row className="mb-5 g-4">
                <Col lg={8}>
                    <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                        <CardBody className="p-4">
                            <h6 className="fw-bold text-slate-800 mb-4">Collection Trend (Daily)</h6>
                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <AreaChart data={payment_trend_chart}>
                                        <defs>
                                            <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                        <XAxis dataKey="payment_date" axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} tickFormatter={s => dayjs(s).format('DD MMM')} />
                                        <YAxis axisLine={false} tickLine={false} fontSize={11} tick={{ fill: '#94a3b8' }} />
                                        <Tooltip contentStyle={{ borderRadius: '12px', border: 0, boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }} />
                                        <Area type="monotone" dataKey="total" stroke="#10b981" strokeWidth={4} fill="url(#colorAmt)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
                <Col lg={4}>
                    <Card className="border-0 shadow-sm rounded-4 h-100">
                        <CardBody className="p-4">
                            <h6 className="fw-bold text-slate-800 mb-4">Payment Methods</h6>
                            <div style={{ width: '100%', height: 320 }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={formattedPieData}
                                            innerRadius={70}
                                            outerRadius={100}
                                            paddingAngle={8}
                                            dataKey="value"
                                            nameKey="name"
                                        >
                                            {formattedPieData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} cornerRadius={6} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend align="center" iconType="circle" />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* ⭐ Detailed Ledger Table */}
            <Card className="border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="bg-white p-4 border-bottom d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold mb-0 text-slate-900">Payment History Ledger</h6>
                    {/* <div className="d-flex gap-2">
                        <Button color="light" size="sm" className="border rounded-pill px-3 fw-bold small">Export Excel</Button>
                    </div> */}
                </div>
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle">
                        <thead className="bg-slate-50 text-slate-400 small uppercase">
                            <tr>
                                <th className="ps-4 py-3">Payment Date</th>
                                <th>Invoice Ref</th>
                                <th>Method</th>
                                <th>Ref / Trans No</th>
                                <th className="text-end pe-4">Amount</th>
                            </tr>
                        </thead>
                        <tbody className="small">
                            {list?.length === 0 ? (
                                <tr><td colSpan="5" className="text-center py-5 text-muted">No payments recorded for this period.</td></tr>
                            ) : list?.map((row, idx) => (
                                <tr key={idx}>
                                    <td className="ps-4 fw-600 text-slate-700">{dayjs(row.payment_date).format("MMM DD, YYYY")}</td>
                                    <td><Badge color="light" className="text-indigo-600 border border-indigo-100">{row.invoice_id}</Badge></td>
                                    <td>
                                        <span className="fw-bold">{row.payment_method}</span>
                                    </td>
                                    <td className="text-slate-500 font-monospace">{row.reference_number || 'N/A'}</td>
                                    <td className="text-end pe-4 fw-bold text-slate-900" style={{ fontSize: '15px' }}>
                                        ${parseFloat(row.amount).toFixed(2)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card>

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
                .report-shell { font-family: 'Plus Jakarta Sans', sans-serif; }
                .fw-600 { font-weight: 600; }
                .ls-1 { letter-spacing: 0.05em; }
                .bg-indigo-50 { background-color: #eef2ff; }
                .text-indigo-600 { color: #4f46e5; }
                .font-monospace { font-family: 'Courier New', Courier, monospace; }
                .table thead th { border-top: 0; font-size: 11px; }
                .table tbody tr:hover { background-color: #f8fafc !important; }
            `}</style>
        </div>
    );
};

export default PaymentReport;